import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";

const SUPABASE_URL = () => globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL;

// The per-item report detail, shown on expand: one line per individual report with
// its reason, free-text, and reporter. Reporter identity lives only on this
// admin-gated view (admin_moderation_report_detail behind permissions() > 0), never
// in the aggregate queue. Fetched on demand so the reporter PII isn't pulled for
// every list row.
function ModerationDetail({ reportableType, reportableId }) {
  const [reports, setReports] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    const params = new URLSearchParams({
      reportable_type: `eq.${reportableType}`,
      reportable_id: `eq.${reportableId}`,
      order: "created_at.desc",
    });
    fetch(`${SUPABASE_URL()}/rest/v1/admin_moderation_report_detail?${params}`, {
      headers: authenticatedHeadersHash(),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`(${res.status})`))))
      .then((data) => active && setReports(data))
      .catch((e) => active && setError(String(e.message || e)));
    return () => {
      active = false;
    };
  }, [reportableType, reportableId]);

  if (error) return <div className="text-danger small">Could not load reports {error}</div>;
  if (reports === null) return <div className="text-muted small">Loading reports…</div>;
  if (reports.length === 0) return <div className="text-muted small">No reports.</div>;

  // Split current (still counting toward the unreviewed total) from already-adjudicated
  // reports (filed at/before the review watermark, unreviewed === false). The `unreviewed`
  // flag uses the same predicate as the queue's count. An ABSENT flag (e.g. a view not yet
  // regenerated) counts as current, so we never wrongly bury reports below the divider.
  const current = reports.filter((r) => r.unreviewed !== false);
  const adjudicated = reports.filter((r) => r.unreviewed === false);

  const renderReport = (r) => (
    <li key={r.report_id} className="mb-1">
      <span className="badge bg-light text-dark border me-1">{r.reason}</span>
      {r.description && <span className="text-body">{r.description} </span>}
      <span className="text-muted">— {r.reporter_name || "Unknown reporter"}</span>
    </li>
  );

  return (
    <div className="small">
      {current.length > 0 && <ul className="list-unstyled mb-0">{current.map(renderReport)}</ul>}
      {adjudicated.length > 0 && (
        <>
          {/* A horizontal rule (with generous vertical space) separates the current
              reports from the already-adjudicated ones, which sit under a single small
              heading — de-emphasised as a group rather than tagged line by line. */}
          {current.length > 0 && <hr className="my-3" />}
          <div
            className="text-uppercase mb-2"
            style={{ fontSize: "0.7rem", letterSpacing: "0.06em", color: "rgba(33, 37, 40, 0.35)" }}
          >
            Previous
          </div>
          <ul className="list-unstyled mb-0" style={{ opacity: 0.45 }}>
            {adjudicated.map(renderReport)}
          </ul>
        </>
      )}
    </div>
  );
}

ModerationDetail.propTypes = {
  reportableType: PropTypes.string.isRequired,
  reportableId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ModerationDetail;
